import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'

/**
 * PageHeader — universele paginakop (R11 design system).
 *
 * Props:
 *   eyebrow      — kleine label boven de titel (optioneel)
 *   title        — hoofdtitel (verplicht)
 *   subtitle     — subtitel onder de titel (optioneel)
 *   action       — ReactNode rechts, bv. primaire knop (optioneel)
 *   onBack       — () => void; toont een terug-knop links (optioneel)
 *   breadcrumb   — array van { label, to? }; toont broodkruimel (optioneel)
 *   className    — extra classes (optioneel)
 *
 * Detail-pagina:  <PageHeader onBack={...} breadcrumb={[...]} title=... />
 * Lijst-pagina:   <PageHeader title=... action={<button.../>} />
 *
 * Let op (B0): index.css gebruikt r11-page-eyebrow/-title/-sub (enkele streep,
 * geen BEM __). `.r11-page-header` is enkel `mb-8` — de flex-layout voor
 * titel-links/actie-rechts staat daarom hier expliciet.
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  action,
  onBack,
  breadcrumb,
  className = '',
}) {
  return (
    <div className={className}>
      {/* Terug + breadcrumb-rij (alleen detail-pagina's) */}
      {(onBack || breadcrumb) && (
        <div className="flex items-center gap-2 mb-3 text-xs text-faint">
          {onBack && (
            <button
              onClick={onBack}
              aria-label="Terug"
              className="flex items-center gap-1 text-muted hover:text-ink transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
              <span className="font-medium">Terug</span>
            </button>
          )}
          {breadcrumb && breadcrumb.length > 0 && (
            <nav aria-label="Broodkruimel" className="flex items-center gap-1.5">
              {onBack && <span className="text-line">·</span>}
              {breadcrumb.map((crumb, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <span aria-hidden="true">/</span>}
                  {crumb.to ? (
                    <Link to={crumb.to} className="hover:text-ink transition-colors">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-muted">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
        </div>
      )}

      {/* Titel-rij — .r11-page-header (mb-8) + flex voor titel/actie */}
      <div className="r11-page-header flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {eyebrow && <p className="r11-page-eyebrow">{eyebrow}</p>}
          <h1 className="r11-page-title">{title}</h1>
          {subtitle && <p className="r11-page-sub">{subtitle}</p>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    </div>
  )
}
